/**
 * ГИБЕЛЬ ОТРЯДА в конце этапа стрельбы и ближнего боя (план — utils/effects/buildUnitDeathCast,
 * запуск — scenes/playUnitDeathEffects). Короткая: итоги боя ждут ее, а они держат весь
 * интерфейс.
 *
 * Отряд вспыхивает белым, вздрагивает, темнеет и оседает в пепел. В этот момент бьют лучи,
 * волна цветом погибшего игрока и осколки; вверх уходят столб света, искры и череп.
 * Чем старше отряд, тем больше всего, а у самых старших вздрагивает и камера.
 *
 * Время — в миллисекундах, размеры — доли размера ячейки.
 */
export const UNIT_DEATH_CAST = {
  hitMs: 110,
  collapseMs: 420,
  tailMs: 620,
  // Пачка гибнет не разом, а цепочкой, но модалка не должна ждать долго
  staggerMs: 80,
  maxStaggerMs: 320,
  // Сверх стольких одновременно — без искр и дыма: на телефоне удары складываются в один кадр
  fullEffectsLimit: 4,
  // Чуть меньше конца анимации: последние искры уже почти погасли
  modalLeadMs: 120,
  // Удары ближнего боя, идущего сразу за стрельбой, начинаются после основной вспышки
  meleePauseMs: 500,
  // Запас за край кадра при проверке, виден ли отряд (доли ячейки)
  viewMarginFactor: 0.5,
  unit: {hitScale: 1.14, endScale: 0.68, sinkFactor: 0.14, tremorFactor: 0.045, tilt: 0.22, ash: 0x2b2622},
  flash: {sizeFactor: 2.3, durationMs: 240},
  stain: {sizeFactor: 2.1, alpha: 0.6},
  rays: {sizeFactor: 2.5, durationMs: 420, turn: 0.5, alpha: 0.9},
  wave: {endFactor: 1.85, durationMs: 460, alpha: 0.95},
  secondWave: {endFactor: 2.5, durationMs: 560, alpha: 0.8, delayMs: 90, minTier: 3},
  shards: {
    countBase: 7,
    countPerTier: 3,
    minReachFactor: 0.8,
    maxReachFactor: 1.6,
    lengthFactor: 0.5,
    thicknessFactor: 0.11,
    lifeMs: 420,
  },
  smoke: {countBase: 3, countPerTier: 1, sizeFactor: 0.72, spreadFactor: 0.45, lifeMs: 720, alpha: 0.62},
  // Душа отряда: столб света уходит вверх и сужается
  soul: {widthFactor: 0.62, coreWidthShare: 0.38, heightFactor: 2, durationMs: 560, startMs: 70},
  stream: {
    countBase: 7,
    countPerTier: 3,
    spawnMs: 300,
    lifeMs: 460,
    minSizeFactor: 0.14,
    maxSizeFactor: 0.26,
    startRadiusFactor: 0.55,
    endRadiusFactor: 0.18,
    turns: 0.7,
    swirlFlatten: 0.3,
    topJitterFactor: 0.3,
    topLiftFactor: 1.1,
  },
  skull: {sizeFactor: 0.62, startMs: 90, durationMs: 840, popMs: 170, startLiftFactor: 0.2, endLiftFactor: 0.95, tint: 0xf5edd9, outline: 0x241a14, outlineScale: 1.2},
  blood: {intensity: 0.85},
  // Вздрагивает камера только от гибели самых старших
  shake: {minTier: 3, durationMs: 130, intensity: 0.0018},
};

/**
 * ГИБЕЛЬ ОТ ГОЛОДА в конце фазы снабжения (план — utils/effects/buildUnitStarvationCast, запуск —
 * scenes/playUnitStarvationEffects). Кто голодает, видно по колосу над отрядом: сначала он золотой
 * и светится, затем тлеет, чернеет и осыпается, а вместе с ним чахнет и отряд. Колос рассыпался —
 * отряд гибнет так же, как в бою, только без крови: его никто не бил.
 *
 * Время — в миллисекундах от начала очереди отряда, размеры — доли размера ячейки.
 */
export const UNIT_STARVATION_CAST = {
  // Колос золотой: игрок успевает понять, что это снабжение
  goldMs: 700,
  // Гибель: все, что после goldMs, колос тлеет и отряд чахнет
  witherMs: 1800,
  // Пачка гибнет цепочкой
  staggerMs: 110,
  maxStaggerMs: 440,
  // Агония вступает, когда колос начал тлеть: последний всхлип приходится на гибель
  agonyDelayMs: 700,
  unit: {withered: 0x8d8a6a, witherScale: 0.84, tremorFactor: 0.02, tremorTurns: 11, droopFactor: 0.05},
  wheat: {
    textureKey: 'supply_white',
    sizeFactor: 0.95,
    liftFactor: 0.95,
    popMs: 260,
    popFromScale: 0.2,
    outline: 0x1d1712,
    outlineScale: 1.14,
    // Золото -> ржавчина -> гниль -> уголь, доли времени тления
    tints: [
      {share: 0, tint: 0xffd34d},
      {share: 0.3, tint: 0xc27a22},
      {share: 0.65, tint: 0x54402a},
      {share: 1, tint: 0x17110c},
    ],
    wiltScale: 0.78,
    wiltTilt: 0.5,
    sinkFactor: 0.14,
    tremorFactor: 0.03,
    tremorTurns: 15,
    // Последний удар: остаток колоса разлетается
    fadeMs: 200,
  },
  // Свет вокруг золотого колоса: сияние и медленные лучи
  glow: {tint: 0xffc93c, sizeFactor: 1.9, alpha: 0.85, pulses: 2, raysSizeFactor: 2.1, raysAlpha: 0.55, raysTurn: 0.5},
  // Тление: багровый свет под колосом, угли и дым
  ember: {
    tint: 0xff6a1a, sizeFactor: 1.15, alpha: 0.8, flickers: 7, startShare: 0.12, endShare: 0.85,
    sparks: {count: 14, lifeMs: 520, sizeFactor: 0.07, spreadFactor: 0.34, riseFactor: 0.65, tints: [0xffb347, 0xff6a1a, 0xffe08a]},
    smoke: {count: 6, lifeMs: 680, sizeFactor: 0.62, spreadFactor: 0.2, riseFactor: 0.65, alpha: 0.5, tint: 0x2d2720},
  },
  // Труха сыплется, пока колос тлеет, а на гибели — все, что от него осталось
  flakes: {count: 16, lifeMs: 640, sizeFactor: 0.085, spreadFactor: 0.36, fallFactor: 1.05, tints: [0x8f6a2c, 0x54402a, 0x2a2118]},
  burst: {count: 18, lifeMs: 620, sizeFactor: 0.1, reachFactor: 0.75, fallFactor: 0.75, tints: [0x54402a, 0x2a2118, 0xc27a22]},
  dust: {count: 5, startShare: 0.15, lifeMs: 620, sizeFactor: 0.5, spreadFactor: 0.3, riseFactor: 0.2, alpha: 0.32, tint: 0xa79d7c},
};

// Сколько поле должно простоять открытым, прежде чем отряд начнет гибнуть, мс: игрок только что
// закрыл модалку и должен успеть заметить такие отряды. За это время успевает прийти и оверлей
// «Ваш ход»: он появляется чуть позже конца фазы (EffectsAutoPlayer)
export const UNIT_STARVATION_FIELD_OPEN_PAUSE_MS = 500;
// Как часто проверять, не открылось ли поле, мс
export const UNIT_STARVATION_POLL_MS = 100;
